/**
 * Shared WCAG contrast maths for picking a readable label colour on an arbitrary fill.
 *
 * The two ink colours are fixed literals (not theme tokens): contrast is computed against
 * these exact values, so a tenant override of a foreground token must not change what we
 * return.
 */

export type Rgb = readonly [number, number, number]

export const CHARCOAL_RGB: Rgb = [52, 57, 59]
/** Fixed literal matching CHARCOAL_RGB. */
export const CHARCOAL_TEXT = "rgb(52 57 59)"
export const WHITE_TEXT = "white"
/** WCAG AA for normal-size text. */
export const AA_TEXT_RATIO = 4.5

function channel(value: number): number {
  const c = value / 255
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

export function rgbLuminance([r, g, b]: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

export function contrastBetween(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

function parseChannel(token: string): number | null {
  const t = token.trim()
  const n = t.endsWith("%") ? (parseFloat(t) / 100) * 255 : parseFloat(t)
  return Number.isFinite(n) ? Math.min(255, Math.max(0, Math.round(n))) : null
}

/**
 * Parse `#rgb`, `#rrggbb`, `rgb(r g b)`, `rgb(r, g, b)` (and `rgba()` when fully opaque).
 * Returns null for anything else (CSS var tokens, hsl(), named colours, translucent fills).
 */
export function parseColor(input: string): Rgb | null {
  const value = input.trim()
  if (value.startsWith("#")) {
    let h = value.slice(1)
    if (h.length === 3) h = [...h].map((c) => c + c).join("")
    if (!/^[0-9a-f]{6}$/i.test(h)) return null
    const n = parseInt(h, 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  const match = /^rgba?\(\s*([^)]+)\)$/i.exec(value)
  if (!match) return null
  const inner = match[1]!
  const slash = inner.indexOf("/")
  const body = slash === -1 ? inner : inner.slice(0, slash)
  const parts = body.includes(",") ? body.split(",") : body.trim().split(/\s+/)
  let alphaToken: string | undefined
  if (slash !== -1) alphaToken = inner.slice(slash + 1)
  else if (parts.length === 4) alphaToken = parts[3]
  if (alphaToken !== undefined) {
    const raw = alphaToken.trim()
    const alpha = raw.endsWith("%") ? parseFloat(raw) / 100 : parseFloat(raw)
    if (!Number.isFinite(alpha) || alpha < 1) return null
  }
  if (parts.length < 3) return null
  const rgb = parts.slice(0, 3).map(parseChannel)
  if (rgb.some((c) => c === null)) return null
  return rgb as unknown as Rgb
}

export interface ReadableInk {
  /** Higher-contrast of charcoal and white on the fill (charcoal when the fill is unparseable). */
  text: string
  /** The opposite colour, for a halo behind the text. Always set. */
  halo: string
  /** Contrast of `text` on the fill, or null when the fill is unparseable. */
  ratio: number | null
  /** True when the fill is unparseable or `text` stays below 4.5:1, so a halo should be drawn. */
  needsHalo: boolean
}

/** Pick charcoal or white for text on `color`, plus the opposite colour as a halo. */
export function readableInkOn(color: string): ReadableInk {
  const rgb = parseColor(color)
  if (!rgb) return { text: CHARCOAL_TEXT, halo: WHITE_TEXT, ratio: null, needsHalo: true }
  const bg = rgbLuminance(rgb)
  const charcoal = contrastBetween(bg, rgbLuminance(CHARCOAL_RGB))
  const white = contrastBetween(bg, 1)
  const useWhite = white > charcoal
  const ratio = useWhite ? white : charcoal
  return {
    text: useWhite ? WHITE_TEXT : CHARCOAL_TEXT,
    halo: useWhite ? CHARCOAL_TEXT : WHITE_TEXT,
    ratio,
    needsHalo: ratio < AA_TEXT_RATIO,
  }
}
